import React, { useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { gsap } from '../../lib/gsapSetup';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import logoWhite from '../../assets/logo/Main_white.png';
import { useTranslation } from '../../i18n/LanguageContext';

export default function Footer() {
  const footerRef = useRef(null);
  const prefersReduced = useReducedMotion();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const FOOTER_LINKS = {
    platform: {
      titleKey: 'footer.platform',
      links: [
        { labelKey: 'footer.features', section: 'features' },
        { labelKey: 'footer.howItWorks', section: 'how-it-works' },
        { labelKey: 'footer.app', section: 'app' },
        { labelKey: 'footer.videos', section: 'video' },
      ],
    },
    legal: {
      titleKey: 'footer.legal',
      links: [
        { labelKey: 'footer.terms', path: '/terms' },
        { labelKey: 'footer.privacy', path: '/privacy' },
        { labelKey: 'footer.cookies', path: '/cookies' },
        { labelKey: 'footer.refund', path: '/refund-policy' },
        { labelKey: 'footer.legalNotice', path: '/legal' },
      ],
    },
    company: {
      titleKey: 'footer.company',
      links: [
        { labelKey: 'footer.reseller', section: 'reseller' },
        { labelKey: 'footer.faq', section: 'faq' },
        {
          labelKey: 'footer.contact',
          href: 'mailto:contact@agodevsolutions.com',
        },
      ],
    },
  };

  useEffect(() => {
    if (prefersReduced || !footerRef.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        footerRef.current,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: footerRef.current,
            start: 'top 95%',
          },
        },
      );
    }, footerRef);

    return () => ctx.revert();
  }, [prefersReduced]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    } else {
      sessionStorage.setItem('scrollToSection', id);
      navigate('/');
    }
  };

  return (
    <footer
      ref={footerRef}
      className='relative bg-[#0A174E] border-t border-white/[0.08]'
    >
      {/* Accent line at top */}
      <div className='accent-line' />

      <div className='section-container py-16 lg:py-20'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8'>
          {/* Brand column */}
          <div className='lg:col-span-2'>
            <Link to='/' className='inline-block mb-5'>
              <img src={logoWhite} alt='LinxExpress' className='h-10 w-auto' />
            </Link>
            <p className='text-white/60 text-sm leading-relaxed max-w-sm mb-6'>
              {t('footer.tagline')}
            </p>
            <div className='flex items-center gap-3'>
              {/* Social icons - minimal */}
              <a
                href='https://www.facebook.com/profile.php?id=61575105842498'
                target='_blank'
                rel='noopener noreferrer'
                className='w-9 h-9 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-white/40 hover:text-secondary hover:border-secondary/30 transition-all'
                aria-label='Facebook'
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='currentColor'
                >
                  <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
                </svg>
              </a>
              <a
                href='https://www.instagram.com/linxexpress.tn/'
                target='_blank'
                rel='noopener noreferrer'
                className='w-9 h-9 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-white/40 hover:text-secondary hover:border-secondary/30 transition-all'
                aria-label='Instagram'
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='currentColor'
                >
                  <path d='M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z' />
                </svg>
              </a>
              <a
                href='https://www.tiktok.com/@linxexpress'
                target='_blank'
                rel='noopener noreferrer'
                className='w-9 h-9 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-white/40 hover:text-secondary hover:border-secondary/30 transition-all'
                aria-label='TikTok'
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='currentColor'
                >
                  <path d='M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z' />
                </svg>
              </a>
            </div>
          </div>

          {/* Link columns */}
          {Object.values(FOOTER_LINKS).map(({ titleKey, links }) => (
            <div key={titleKey}>
              <h4 className='font-display font-semibold text-sm text-white mb-5 tracking-wide'>
                {t(titleKey)}
              </h4>
              <ul className='space-y-3'>
                {links.map(({ labelKey, path, section, href }) => (
                  <li key={labelKey}>
                    {path ? (
                      <Link
                        to={path}
                        className='text-sm text-white/60 hover:text-secondary transition-colors duration-200'
                      >
                        {t(labelKey)}
                      </Link>
                    ) : href ? (
                      <a
                        href={href}
                        className='text-sm text-white/60 hover:text-secondary transition-colors duration-200'
                      >
                        {t(labelKey)}
                      </a>
                    ) : (
                      <button
                        onClick={() => scrollTo(section)}
                        className='text-sm text-white/60 hover:text-secondary transition-colors duration-200'
                      >
                        {t(labelKey)}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className='mt-16 pt-8 border-t border-white/[0.08] flex flex-col sm:flex-row items-center justify-between gap-4'>
          <p className='text-xs text-white/40'>
            © {new Date().getFullYear()} Agodev Solutions SARL.{' '}
            {t('footer.rights')}
          </p>
          <div className='flex items-center gap-6'>
            <Link
              to='/terms'
              className='text-xs text-white/40 hover:text-white/70 transition-colors'
            >
              {t('footer.cgu')}
            </Link>
            <Link
              to='/privacy'
              className='text-xs text-white/40 hover:text-white/70 transition-colors'
            >
              {t('footer.privacy')}
            </Link>
            <Link
              to='/cookies'
              className='text-xs text-white/40 hover:text-white/70 transition-colors'
            >
              {t('footer.cookies')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
